import React, { useState } from "react";
import PropTypes from "prop-types";
import { Button, Card, CardHeader, CardBody, Row, Col } from "reactstrap";
import mixpanelService from "common/mixpanel-service";
import pageData from "common/page-data";
import CreativeTemplatePropType from "common/prop-types/creative-template-prop-type";
import { ADMIN_PAYMENT_METHOD } from "common/components/admin-payment-form-proptypes";
import CampaignGroupPropType from "../campaign-group-prop-type";
import CampaignGroupImagePreview from "../campaign-group-image-preview";
import CampaignAdPreviewModal from "./campaign-ad-preview-modal";
import CampaignDetails from "./campaign-details";
import CampaignConfiguration from "./campaign-configuration";
import CampaignActions from "./campaign-actions";
import ImpressionBoostPurchaseFlow from "./impression-boost-purchase-flow";
import "../campaign-group.styles.scss";

const propTypes = {
  campaignGroup: CampaignGroupPropType.isRequired,
  creativeTemplates: PropTypes.arrayOf(CreativeTemplatePropType).isRequired,
  facebookPage: PropTypes.shape({
    name: PropTypes.string,
    picture: PropTypes.string
  }),
  campaignPackage: PropTypes.shape({
    packageLabel: PropTypes.string,
    packageDetails: PropTypes.string,
    startsAt: PropTypes.string,
    endsAt: PropTypes.string,
    newRenewalPlan: PropTypes.string,
    renewalDate: PropTypes.string
  }).isRequired,
  destinationURL: PropTypes.string,
  venue: PropTypes.shape({
    name: PropTypes.string,
    address: PropTypes.string
  }),

  targetingAreas: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      radius: PropTypes.number
    })
  ).isRequired,
  vistarPostalCodes: PropTypes.arrayOf(
    PropTypes.shape({
      postalCode: PropTypes.string.isRequired,
      count: PropTypes.number,
      category: PropTypes.string
    })
  ),
  uniqueContacts: PropTypes.number,
  campaignActions: PropTypes.shape({
    editAdCreativeLink: PropTypes.string,
    editDestinationPageLink: PropTypes.string,
    editFacebookPageLink: PropTypes.string,
    editTargetAudienceLink: PropTypes.string,
    editGeographiesLink: PropTypes.string,
    copyAdLink: PropTypes.string,
    editBillingInfoLink: PropTypes.string,
    networkConsoleLinks: PropTypes.arrayOf(
      PropTypes.shape({ label: PropTypes.string, url: PropTypes.string })
    )
  }),
  onRefetch: PropTypes.func,
  adminPaymentMethod: ADMIN_PAYMENT_METHOD,
  clientView: PropTypes.bool
};

const CampaignCard = ({
  campaignGroup,
  creativeTemplates,
  facebookPage = {},
  campaignPackage,
  destinationURL = null,
  venue = null,
  targetingAreas,
  vistarPostalCodes = [],
  uniqueContacts = null,
  campaignActions = {},
  onRefetch = () => {},
  adminPaymentMethod = null,
  clientView = false
}) => {
  const [isAdPreviewModalOpen, setIsAdPreviewModalOpen] = useState(false);
  const normalizedVistarZips = vistarPostalCodes.map(({ postalCode, ...rest }) => ({
    ...rest,
    postal_code: postalCode
  }));
  const purchasedZips = [...(campaignGroup.purchased_zips || []), ...normalizedVistarZips];

  const handlePreviewAd = () => {
    mixpanelService.track("Campaign.PreviewMyAd", {
      ...pageData.get("mixpanelData"),
      campaignId: campaignGroup.campaign_id,
      campaignType: campaignGroup.campaign_type
    });
    setIsAdPreviewModalOpen(true);
  };

  return (
    <Row className="mb-3 mx-0">
      <Col className="px-0">
        <Card className="campaign-card-card">
          <CardHeader>
            <strong>Your Ad</strong>
            <span className="text-muted">
              <span className="mx-2">•</span>
              {campaignGroup.campaign_label}
            </span>
          </CardHeader>
          <CardBody>
            <Row>
              <Col xs="12" lg="3" className="pl-0 pb-3 mb-3 pb-lg-0 mb-lg-0">
                <h6 className="text-uppercase font-weight-semibold font-size-small mb-3">
                  Ad Preview
                </h6>
                <div className="text-center">
                  <CampaignGroupImagePreview campaignGroup={campaignGroup} />
                </div>
                {creativeTemplates.length > 0 && (
                  <div className="d-flex mt-3 justify-content-center">
                    <Button
                      outline
                      color="secondary"
                      size="sm"
                      className="btn-sharp w-100"
                      onClick={handlePreviewAd}
                    >
                      Preview My Ad
                    </Button>
                  </div>
                )}
              </Col>
              <Col xs="12" lg={clientView ? "9" : "6"} className="pl-0 pb-3 mb-3 pb-lg-0 mb-lg-0">
                <CampaignDetails campaignPackage={campaignPackage} purchasedZips={purchasedZips} />
                <CampaignConfiguration
                  destinationURL={destinationURL}
                  venue={venue}
                  targetingAreas={targetingAreas}
                  uniqueContacts={uniqueContacts}
                />
                {!clientView && (
                  <ImpressionBoostPurchaseFlow
                    campaignGroup={campaignGroup}
                    adminPaymentMethod={adminPaymentMethod}
                  />
                )}
              </Col>
              {!clientView && (
                <Col xs="12" lg="3" className="pl-0 pb-3 mb-3 pb-lg-0 mb-lg-0">
                  <CampaignActions
                    campaignGroup={campaignGroup}
                    campaignActions={campaignActions}
                    destinationURL={destinationURL}
                    onRefetch={onRefetch}
                    adminPaymentMethod={adminPaymentMethod}
                  />
                </Col>
              )}
            </Row>
          </CardBody>
        </Card>
      </Col>
      {creativeTemplates.length > 0 && (
        <CampaignAdPreviewModal
          isOpen={isAdPreviewModalOpen}
          onClose={() => setIsAdPreviewModalOpen(false)}
          templates={creativeTemplates}
          facebookPage={facebookPage}
          productCode={campaignGroup.short_type || ""}
        />
      )}
    </Row>
  );
};

CampaignCard.propTypes = propTypes;

export default CampaignCard;
